'use client'

import { $getRoot, $isTextNode, $isParagraphNode, $getSelection, $isRangeSelection } from 'lexical'
import { $isMentionNode } from './MentionNode'

function $isParagraphEmpty(paragraph) {
    const children = paragraph.getChildren()
    if (children.length === 0) return true
    if (children.length === 1 && $isTextNode(children[0]) && children[0].getTextContent().trim() === '') return true
    return false
}

function $getMentionsInParagraph(paragraph) {
    return paragraph.getChildren().filter($isMentionNode)
}

/**
 * Builds a human-readable name for a segment/scene mention by collecting
 * the surrounding paragraph text after the mention, until an empty line.
 */
function $getMentionTitle(paragraph, mentionNode, allParagraphs, paragraphIndex) {
    const collect = (para, startAfterMention) => {
        let started = !startAfterMention
        const parts = []
        for (const child of para.getChildren()) {
            if (!started) {
                if (child === mentionNode) started = true
                continue
            }
            if ($isMentionNode(child)) continue
            const text = child.getTextContent()
            if (text) parts.push(text)
        }
        return parts.join('').trim()
    }

    let title = collect(paragraph, true)
    if (title) return title

    for (let i = paragraphIndex + 1; i < allParagraphs.length; i++) {
        const next = allParagraphs[i]
        if ($isParagraphEmpty(next)) break
        const txt = collect(next, false)
        if (txt) return txt
    }
    return ''
}

/**
 * Determines the active editing context for a given paragraph based on
 * which mention node governs it. Used for inline styling, not breadcrumb.
 *
 * Returns: 'segment' | 'scene' | 'description' | 'character' | null
 */
export function $getContextForParagraph(paragraph, allParagraphs, paragraphIndex) {
    const mentions = $getMentionsInParagraph(paragraph)

    for (const mention of mentions) {
        const mentionType = mention.getMentionType()
        const mentionId = mention.getMentionId()
        if (mentionType === 'command') {
            if (mentionId === 'segment') return 'segment'
            if (mentionId === 'scene') return 'scene'
            if (mentionId === 'description') return 'description'
        } else if (mentionType === 'character') {
            return 'character'
        }
    }

    for (let i = paragraphIndex - 1; i >= 0; i--) {
        const prev = allParagraphs[i]
        if ($isParagraphEmpty(prev)) return null

        const prevMentions = $getMentionsInParagraph(prev)
        for (const mention of prevMentions) {
            const mentionType = mention.getMentionType()
            const mentionId = mention.getMentionId()
            if (mentionType === 'command') {
                if (mentionId === 'segment') return 'segment'
                if (mentionId === 'scene') return 'scene'
                if (mentionId === 'description') return 'description'
            } else if (mentionType === 'character') {
                return 'character'
            }
        }
    }

    return null
}

/**
 * Returns the active context mode at the current cursor position
 * (single-string form, kept for backwards compatibility).
 */
export function $getContextAtCursor() {
    const ctx = $getFullContextAtCursor()
    return ctx?.mode ?? null
}

/**
 * Returns the full hierarchical context at the current cursor position:
 *   {
 *     segment:     { id, name } | null,
 *     scene:       { id, name } | null,
 *     description: { id } | null,
 *     character:   { id, name } | null,
 *     mode:        'segment' | 'scene' | 'description' | 'character' | null,
 *   }
 *
 * - segment / scene persist across empty lines until the next segment/scene
 *   mention (a new segment resets the scene).
 * - description / character only apply within the current "block"
 *   (current paragraph back to the previous empty line or segment/scene).
 * - mode is the most-specific context for the cursor's paragraph.
 *
 * Must be called inside editor.getEditorState().read() or editor.update().
 */
export function $getFullContextAtCursor() {
    const empty = { segment: null, scene: null, description: null, character: null, mode: null }

    const selection = $getSelection()
    if (!$isRangeSelection(selection)) return empty

    const anchor = selection.anchor
    const anchorNode = anchor.getNode()
    const paragraph = $isParagraphNode(anchorNode) ? anchorNode : anchorNode.getParent()
    if (!paragraph || !$isParagraphNode(paragraph)) return empty

    const root = $getRoot()
    const paragraphs = root.getChildren().filter($isParagraphNode)
    const pIndex = paragraphs.indexOf(paragraph)
    if (pIndex === -1) return empty

    let segment = null
    let scene = null

    for (let i = 0; i <= pIndex; i++) {
        const para = paragraphs[i]
        for (const child of para.getChildren()) {
            if (!$isMentionNode(child)) continue
            const mentionType = child.getMentionType()
            const mentionId = child.getMentionId()
            if (mentionType !== 'command') continue
            if (mentionId === 'segment') {
                segment = {
                    id: child.getObjectId(),
                    name: $getMentionTitle(para, child, paragraphs, i) || 'Segment',
                }
                scene = null
            } else if (mentionId === 'scene') {
                scene = {
                    id: child.getObjectId(),
                    name: $getMentionTitle(para, child, paragraphs, i) || 'Scene',
                }
            }
        }
    }

    let description = null
    let character = null

    for (let i = pIndex; i >= 0; i--) {
        const para = paragraphs[i]

        if (i < pIndex && $isParagraphEmpty(para)) break

        let blockBoundary = false
        for (const child of para.getChildren()) {
            if (!$isMentionNode(child)) continue
            const mentionType = child.getMentionType()
            const mentionId = child.getMentionId()

            if (mentionType === 'command' && mentionId === 'description' && !description) {
                description = { id: child.getObjectId() }
            } else if (mentionType === 'character' && !character) {
                character = { id: mentionId, name: child.getMentionName() }
            } else if (mentionType === 'command' && (mentionId === 'segment' || mentionId === 'scene')) {
                blockBoundary = true
            }
        }

        if (blockBoundary) break
    }

    const mode = $getContextForParagraph(paragraph, paragraphs, pIndex)

    return { segment, scene, description, character, mode }
}
